/**
 * El puente entre la pestaña «Mis objetos» y la tabla `ObjetosPlanta`.
 *
 * Reparto de trabajo, decidido así a propósito:
 *
 *  - **Lo mío vive en `localStorage`** (ver `leerPropios` en `muebles.tsx`).
 *    Es lo que hace que el editor siga guardando objetos sin backend —la
 *    página de pruebas, un deploy sin la tabla todavía aplicada, el celular
 *    sin señal— y que una pieza recién importada aparezca al instante.
 *  - **Lo COMPARTIDO vive sólo en el servidor.** Se trae al abrir y no se
 *    copia a este navegador: si el equipo despublica una pieza, tiene que
 *    desaparecer, y una copia local la dejaría viva para siempre acá.
 *
 * Todo lo que habla con la red falla en silencio hacia «no hay servidor»:
 * nunca al revés. Un editor que no abre porque la API no contestó sería peor
 * que un editor sin catálogo compartido.
 */

// La ÚNICA línea que cambia entre arqui-flow y AdVentas: cada repo tiene su
// propia forma de hablar con `/api` —acá un cliente único, allá un módulo por
// recurso— y el resto de este archivo se espeja tal cual.
import { api } from '@/api/client';
const puerta = api.objetosPlanta;

import { fijarCompartidos, fijarTintaElectrico, type ModeloMueble } from './muebles';
import type { Trazo } from './importar-simbolo';

/** Lo que devuelve `GET /api/objetos-planta`. Espejo del `ObjetoDto` de C#. */
export interface ObjetoPlantaDto {
  clave: string;
  nombre: string;
  grupo: string;
  ancho: number;
  fondo: number;
  /** El `Trazo` serializado, o null si la pieza usa un símbolo de fábrica. */
  trazo: string | null;
  simbolo: string;
  electrico: boolean;
  pegaPared: boolean;
  /** En METROS sobre el piso acabado. null = la pieza no rotula altura. */
  alturaDefecto: number | null;
  compartido: boolean;
  mio: boolean;
}

export interface ObjetoPlantaEntrada {
  clave: string;
  nombre: string;
  grupo: string;
  ancho: number;
  fondo: number;
  trazo: string | null;
  simbolo: string;
  electrico: boolean;
  pegaPared: boolean;
  alturaDefecto: number | null;
}

/** La convención de dibujo del equipo. Hoy, el color de la pluma eléctrica. */
export interface AjustesPlanta {
  tintaElectrico: string;
}

/** El dibujo guardado como texto, o `undefined` si no se puede leer. */
function leerTrazo(crudo: string | null): Trazo | undefined {
  if (!crudo) return undefined;
  try {
    const t = JSON.parse(crudo);
    // Se verifica la forma y no se confía en la columna: la escribió una
    // versión anterior de este mismo código y un `d` que no es lista rompería
    // el pintado de TODA la vitrina, no sólo de esta pieza.
    if (Array.isArray(t?.d) && Array.isArray(t?.caja) && t.caja.length === 4) return t as Trazo;
  } catch { /* una pieza sin dibujo cae al símbolo de fábrica, que es legible */ }
  return undefined;
}

function aFicha(o: ObjetoPlantaDto): ModeloMueble {
  const trazo = leerTrazo(o.trazo);
  return {
    clave: o.clave,
    nombre: o.nombre,
    w: o.ancho,
    h: o.fondo,
    simbolo: o.simbolo,
    grupo: o.grupo,
    ...(trazo ? { trazo } : null),
    // Los tres van con `...(cond ? {…} : null)` y no como `electrico: o.electrico`
    // porque `ModeloMueble` los declara opcionales y literales (`electrico?: true`):
    // escribir `false` donde el catálogo de fábrica escribe «ausente» haría que
    // dos fichas equivalentes no se parezcan al compararlas.
    ...(o.electrico ? { electrico: true as const } : null),
    ...(o.pegaPared ? { pegaPared: true as const } : null),
    ...(o.alturaDefecto != null ? { alturaDefecto: o.alturaDefecto } : null),
  };
}

export const aEntrada = (m: ModeloMueble): ObjetoPlantaEntrada => ({
  clave: m.clave,
  nombre: m.nombre,
  grupo: m.grupo,
  ancho: m.w,
  fondo: m.h,
  trazo: m.trazo ? JSON.stringify(m.trazo) : null,
  simbolo: m.simbolo,
  electrico: m.electrico === true,
  pegaPared: m.pegaPared === true,
  alturaDefecto: m.alturaDefecto ?? null,
});

/**
 * Trae el catálogo compartido y dice si este usuario puede publicar.
 *
 * Devuelve `false` ante cualquier problema —sin tabla, sin sesión, sin red—
 * y eso APAGA el botón de publicar, que es el lado seguro: un botón que
 * promete lo que el servidor va a rechazar enseña que la función no sirve.
 */
export async function traerObjetos(): Promise<boolean> {
  try {
    const r = await puerta.list();
    // La tinta ANTES del catálogo: `fijarCompartidos` es lo que dispara el
    // repintado de la vitrina, así que si el color llegara después, la
    // simbología alcanzaría a pintarse una vez del color viejo.
    if (r.ajustes?.tintaElectrico) fijarTintaElectrico(r.ajustes.tintaElectrico);
    fijarCompartidos(r.objetos.filter((o) => o.compartido).map(aFicha));
    return r.puedePublicar;
  } catch {
    fijarCompartidos([]);
    return false;
  }
}

/**
 * Cambia la convención de dibujo del equipo.
 *
 * No falla en silencio como el resto del archivo, a propósito: las otras
 * llamadas caen a «no hay servidor» porque el editor puede seguir trabajando
 * sin catálogo compartido. Esta no — si el color no se guardó, decirlo en voz
 * alta es lo único que evita que Daniel crea que el equipo ya lo tiene.
 */
export async function guardarAjustes(a: AjustesPlanta): Promise<void> {
  const r = await puerta.guardarAjustes(a);
  fijarTintaElectrico(r.tintaElectrico);
}

/**
 * Sube la pieza y le suelta el dueño, en ese orden.
 *
 * Son dos llamadas porque hasta hoy la pieza sólo existía en este navegador:
 * publicar es subirla primero. Si ya estaba subida el servidor contesta 409 y
 * eso NO es un error acá — significa que el primer paso ya estaba hecho.
 */
export async function publicarObjeto(ficha: ModeloMueble): Promise<void> {
  try {
    await puerta.create(aEntrada(ficha));
  } catch (e) {
    if ((e as { status?: number }).status !== 409) throw e;
  }
  await puerta.publicar(ficha.clave);
  await traerObjetos();
}

/**
 * Corrige una pieza del catálogo del equipo.
 *
 * La `clave` NO viaja en el cuerpo del cambio aunque `aEntrada` la incluya: el
 * servidor la toma de la ruta y es inmutable por diseño —es lo que las piezas
 * ya puestas en planos terminados guardan como `Mueble.tipo`, así que cambiarla
 * las dejaría huérfanas—. Acá se corrige el nombre, el grupo y las medidas.
 */
export async function corregirCompartido(clave: string, ficha: ModeloMueble): Promise<void> {
  await puerta.update(clave, aEntrada(ficha));
  await traerObjetos();
}

/**
 * Saca una pieza del catálogo del equipo.
 *
 * El borrado del servidor es LÓGICO (`EsActivo = 0`), así que los planos que ya
 * la tienen puesta siguen dibujándola; lo que desaparece es la oferta en la
 * vitrina. Es la misma promesa que hace borrar una pieza propia.
 */
export async function quitarCompartido(clave: string): Promise<void> {
  await puerta.remove(clave);
  await traerObjetos();
}
